import { ColumnDef } from '@tanstack/react-table'
import classNames from 'classnames'
import {
  CheckCircle,
  CheckCircleSolid,
  Circle,
  EditPencil,
  MenuScale,
  PasteClipboard,
  WarningCircle,
} from 'iconoir-react'
import { compact } from 'lodash'
import moment from 'moment'
import { LazyContactCell } from 'app/components/LazyContactCell'
import { LazyStaffCell } from 'app/components/LazyStaffCell'
import { DatePickerCell } from 'app/components/table/DatePickerCell'
import { LazyStaffAssignCell } from 'app/components/table/LazyStaffAssignCell'
import { LazyUserAssignCell } from 'app/components/table/LazyUserAssignCell'
import { graphql } from 'app/generated/gql'
import { gqlFetchList, gqlMutation } from 'app/lib/graphqlClient'
import { isTaskOverdue } from 'app/lib/taskDueDate'
import { staffMembersFetcher } from 'app/lib/fetchers/staffMembersFetcher'

const UPDATE_TASK_MUTATION = graphql(`
  mutation UpdateTaskTable($id: ID!, $input: UpdateTaskInput!) {
    updateTask(id: $id, input: $input) {
      id
    }
  }
`)
import { Tooltip } from 'app/components/Tooltip'
import {
  DateRangeOperator,
  FilterOperator,
  FilterValueType,
  FilterableColumn,
  PredefinedFilter,
  addDefaultFilterProperties,
} from 'app/filter.utils'
import type { TaskJson } from 'app/types/entities'
import { notifySuccess } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { OptionType } from 'types/OptionType'

export const getFilterableColumns = (preAppliedFilters?: PredefinedFilter): FilterableColumn[] => {
  const filters = compact([
    {
      attribute: `dueDate`,
      displayName: `Due Date`,
      type: FilterValueType.rangeDate,
      dateOperator: DateRangeOperator.this_month,
      defaultValue: [moment().startOf(`month`).format(`YYYY-MM-DD`), moment().endOf(`month`).format(`YYYY-MM-DD`)],
      operator: `between` as FilterOperator,
      includeToday: true,
      includeFuture: true,
    },
    {
      attribute: `locationId`,
      displayName: `Location`,
      type: FilterValueType.id,
      optionsSource: `/api/locations`,
      uniqOperator: `in` as FilterOperator,
      standalone: true,
    },
    {
      attribute: `ownerId`,
      displayName: `Owner`,
      type: FilterValueType.id,
      fetcher: (params) => staffMembersFetcher(params),
      uniqOperator: `in` as FilterOperator,
    },
    {
      attribute: `roleId`,
      displayName: `Assigned role`,
      type: FilterValueType.id,
      fetcher: async (params) => {
        const roles = await gqlFetchList<{ id: string; name: string }>(
          `roles`,
          { pagination: { page: 0, limit: 100 } },
          `id name`,
        )
        const hits = roles.hits.filter((role) => role.name.toLowerCase().includes((params.query ?? ``).toLowerCase()))
        return { ...roles, hits, total: hits.length }
      },
      uniqOperator: `in` as FilterOperator,
    },
    {
      attribute: `createdById`,
      displayName: `Created By`,
      type: FilterValueType.id,
      fetcher: (params) => staffMembersFetcher(params),
      uniqOperator: `in` as FilterOperator,
    },
    {
      attribute: `status`,
      displayName: `Status`,
      type: FilterValueType.boolean,
      uniqOperator: `exactly` as FilterOperator,
      options: [
        { id: `open`, value: `open`, name: `Open` },
        { id: `closed`, value: `closed`, name: `Closed` },
      ],
    },
  ])

  return filters?.map((filter) => ({ ...filter, ...addDefaultFilterProperties(filter.attribute, preAppliedFilters) }))
}

export const defaultColumns = (
  useStaffTable: boolean,
  onCheck: (taskId: string, moveToComplete: boolean) => void,
  updateTask: (task: TaskJson) => Promise<TaskJson>,
  onEditTaskClick: (task: TaskJson) => void,
  onContactClick?: (contactId: string, highlightTaskId?: string) => void,
): ColumnDef<TaskJson, any>[] => [
  {
    id: `Done`,
    accessorKey: `status`,
    header: ``,
    enableResizing: false,
    enableSorting: false,
    enableHiding: false,
    size: 36,
    minSize: 36,
    maxSize: 36,
    meta: {
      defaultVisible: true,
      pinned: true,
      ControlledComponent: (row) => {
        if (!row) return <></>
        const isClosed = row.status === `closed`
        return (
          <button
            type="button"
            aria-label={isClosed ? `Mark as open` : `Mark as complete`}
            title={isClosed ? `Mark as open` : `Mark as complete`}
            className="flex h-full w-full items-center justify-center"
            onClick={(e) => {
              e.stopPropagation()
              onCheck(row.id!, !isClosed)
            }}
          >
            {isClosed ? (
              <CheckCircleSolid className="h-4 w-4 text-success-default" />
            ) : (
              <Circle className="h-4 w-4 text-minimal transition-colors hover:text-success-default" strokeWidth={2} />
            )}
          </button>
        )
      },
    },
  },
  {
    id: `Task Name`,
    accessorKey: `title`,
    header: `Task Name`,
    enableResizing: true,
    meta: {
      ControlledComponent: (row) => {
        if (!row) return <></>
        const isClosed = row.status === `closed`
        return (
          <div className="flex items-center justify-between gap-x-2 px-2">
            <div className="flex min-w-0 items-center gap-x-2">
              <span
                className={classNames(
                  `overflow-hidden text-ellipsis whitespace-nowrap`,
                  isClosed && `text-minimal line-through`,
                )}
              >
                {row.title}
              </span>
              {row.description && (
                <Tooltip
                  message={row.description}
                  placement="bottom"
                  tooltipClassName="max-w-[256px] max-h-[62px] line-clamp-3"
                >
                  <MenuScale className="hidden h-4 w-4 text-subtle group-hover:block" />
                </Tooltip>
              )}
            </div>
            <button
              type="button"
              title="Edit task"
              aria-label="Edit task"
              className="hidden h-6 w-6 shrink-0 items-center justify-center rounded text-subtle hover:bg-surface-muted hover:text-strong group-hover:flex"
              onClick={(e) => {
                e.stopPropagation()
                onEditTaskClick(row)
              }}
            >
              <EditPencil className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          </div>
        )
      },
      defaultVisible: true,
      pinned: true,
      sortKey: `title.keyword`,
    },
    enableHiding: false,
    minSize: 100,
  },
  {
    id: `Status`,
    accessorKey: `status`,
    header: `Status`,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row) => {
        let label = `Open`
        if (row?.status === `closed`) {
          label = `Completed`
        } else if (isTaskOverdue({ dueDate: row?.dueDate })) {
          label = `Overdue`
        }

        return (
          <div className="inline-flex items-center justify-between gap-2 p-4">
            {label === `Overdue` && <WarningCircle className="h-3 w-3 text-red-500" strokeWidth={3} />}
            {label === `Completed` && <CheckCircle className="h-3 w-3 text-green-500" strokeWidth={3} />}
            {label === `Open` && <PasteClipboard className="h-3 w-3" strokeWidth={3} />}
            <span>{label}</span>
          </div>
        )
      },
    },
    enableSorting: false,
    minSize: 80,
  },
  {
    id: `Contact`,
    accessorKey: `contactId`,
    header: `Contact`,
    meta: {
      defaultVisible: true,
      sortKey: `contactId.keyword`,
      ControlledComponent: (row) => {
        const contactId = row!.contactId
        if (!contactId) return <></>
        return (
          <div
            role={onContactClick ? `button` : undefined}
            tabIndex={onContactClick ? 0 : undefined}
            className={classNames(
              `flex w-full min-w-0 overflow-hidden [&>div]:min-w-0 [&>div]:overflow-hidden [&_span]:overflow-hidden [&_span]:text-ellipsis [&_span]:whitespace-nowrap`,
              onContactClick && `cursor-pointer`,
            )}
            onClick={
              onContactClick
                ? (e) => {
                    e.stopPropagation()
                    onContactClick(contactId, row!.id!)
                  }
                : undefined
            }
            onKeyDown={
              onContactClick
                ? (e) => {
                    if (e.key === `Enter` || e.key === ` `) {
                      e.preventDefault()
                      e.stopPropagation()
                      onContactClick(contactId, row!.id!)
                    }
                  }
                : undefined
            }
          >
            <LazyContactCell contactId={contactId} loadingIndicator={true} />
          </div>
        )
      },
    },
  },
  {
    id: `Owner`,
    accessorKey: `ownerId`,
    header: `Owner`,
    meta: {
      defaultVisible: true,
      sortKey: `ownerId.keyword`,
      ControlledComponent: (row) => (
        <div className="flex w-full min-w-0 overflow-hidden [&>span]:block [&>span]:min-w-0 [&>span]:overflow-hidden">
          {useStaffTable ? (
            <LazyUserAssignCell
              id={row!.ownerId}
              firstNameInitial
              workspaceId={row!.workspaceId!}
              locationId={row!.locationId!}
              fetcher={staffMembersFetcher}
              onChange={(val?: OptionType) => {
                bg(async () => {
                  if (!val?.value) return
                  if (!row?.id) return
                  await gqlMutation(UPDATE_TASK_MUTATION, { id: row.id, input: { ownerId: val.value } })
                  notifySuccess(`Task owner updated`)
                })
              }}
            />
          ) : (
            <LazyStaffAssignCell
              id={row!.ownerId}
              firstNameInitial
              workspaceId={row!.workspaceId!}
              locationId={row!.locationId!}
              onChange={(val?: OptionType) => {
                bg(async () => {
                  if (!val?.value) return
                  if (!row?.id) return
                  await gqlMutation(UPDATE_TASK_MUTATION, { id: row.id, input: { ownerId: val.value } })
                  notifySuccess(`Task owner updated`)
                })
              }}
            />
          )}
        </div>
      ),
    },
  },
  {
    id: `Due Date`,
    accessorKey: `dueDate`,
    header: `Due Date`,
    minSize: 120,
    meta: {
      defaultVisible: true,
      sortKey: `dueDate`,
      ControlledComponent: (row) => (
        <div className="flex w-full min-w-0 overflow-hidden">
          <DatePickerCell
            value={row!.dueDate}
            onDateChange={(val: Date) => {
              bg(async () => {
                if (!row?.id) return
                await gqlMutation(UPDATE_TASK_MUTATION, { id: row.id, input: { dueDate: val.toISOString() } })
                notifySuccess(`Task due date updated`)
              })
            }}
            className={classNames(isTaskOverdue({ dueDate: row!.dueDate }) ? `text-red-500` : undefined)}
            dateOnly
            showIcon
          />
        </div>
      ),
    },
  },
  {
    id: `Created By`,
    accessorKey: `createdById`,
    header: `Created By`,
    cell: (props) => <LazyStaffCell userId={props.getValue()} />,
    meta: {
      defaultVisible: true,
      sortKey: `createdById.keyword`,
    },
  },
  // {
  //   id: `menu`,
  //   header: ``,
  //   size: 60,
  //   minSize: 60,
  //   maxSize: 60,
  //   enableResizing: false,
  //   cell: function Action({ row, column }) {
  //     useEffect(() => {
  //       column.pin(`right`)
  //       return () => column.pin(false)
  //     }, [])
  //     return (
  //       <div className="ml-3 flex h-5 items-center">
  //         <MoreVert className="h-4 w-4 cursor-pointer text-trn-900" onClick={() => onEditTaskClick(row.original)} />
  //       </div>
  //     )
  //   },
  //   meta: {
  //     defaultVisible: true,
  //     pinned: false,
  //   },
  //   enableSorting: false,
  //   enableHiding: false,
  // },
]
